import { getApiBase } from "@/lib/env";

export const CUSTOMER_VOICE_SOUND_SETTING_KEY = "customer_voice.sound_admin_url";
export const CUSTOMER_VOICE_ALERT_ENABLED_KEY = "customer_voice.alert_enabled";
export const CUSTOMER_VOICE_SOUND_ON_STORAGE_KEY = "admin.customerVoice.soundOn.v1";
export const CUSTOMER_VOICE_NEW_TICKET_EVENT = "admin:customer-voice:new";
export const CUSTOMER_VOICE_SETTINGS_CHANGED_EVENT = "admin:customer-voice:settings-changed";

export type CustomerVoiceRealtimePayload = {
  id: string;
  ticketNo: string;
  contactName: string;
  feedbackType: string;
  category?: string | null;
  timestampUtc: string;
};

let sharedAudioCtx: AudioContext | null = null;
let unlockBound = false;

function getAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const Ctx =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return null;
  if (!sharedAudioCtx || sharedAudioCtx.state === "closed") {
    sharedAudioCtx = new Ctx();
  }
  return sharedAudioCtx;
}

/** Call once after a user gesture so later alert beeps are allowed by the browser. */
export function unlockCustomerVoiceAudio() {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === "suspended") {
    void ctx.resume().catch(() => undefined);
  }
}

/** Bind a one-time document click/keydown unlock (safe to call multiple times). */
export function ensureCustomerVoiceAudioUnlockListener() {
  if (typeof window === "undefined" || unlockBound) return;
  unlockBound = true;
  const unlock = () => {
    unlockCustomerVoiceAudio();
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
  };
  window.addEventListener("pointerdown", unlock, { once: true, passive: true });
  window.addEventListener("keydown", unlock, { once: true });
}

function resolveMediaUrl(url: string): string {
  const u = url.trim();
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  return `${getApiBase()}${u.startsWith("/") ? u : `/${u}`}`;
}

async function playBeep() {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      await ctx.resume();
    }
    if (ctx.state !== "running") return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.value = 0.2;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const now = ctx.currentTime;
    osc.start(now);
    osc.stop(now + 0.22);
  } catch {
    /* ignore autoplay / unsupported */
  }
}

function playConfiguredSound(url: string) {
  const u = resolveMediaUrl(url);
  if (!u) {
    void playBeep();
    return;
  }
  if (/\.(mp3|wav|ogg|m4a)(\?|$)/i.test(u) || u.includes("audio") || u.includes("/sounds/")) {
    const audio = new Audio(u);
    void audio.play().catch(() => {
      void playBeep();
    });
    return;
  }
  void playBeep();
}

export function playCustomerVoiceAlert(soundUrl: string) {
  unlockCustomerVoiceAudio();
  if (soundUrl.trim()) playConfiguredSound(soundUrl);
  else void playBeep();
}

export function readCustomerVoiceSoundOn(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const raw = window.localStorage.getItem(CUSTOMER_VOICE_SOUND_ON_STORAGE_KEY);
    if (raw === "false") return false;
    if (raw === "true") return true;
  } catch {
    /* ignore */
  }
  return true;
}

export function writeCustomerVoiceSoundOn(on: boolean) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CUSTOMER_VOICE_SOUND_ON_STORAGE_KEY, on ? "true" : "false");
  } catch {
    /* ignore */
  }
}

export function dispatchCustomerVoiceNewTicket(payload: CustomerVoiceRealtimePayload) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CUSTOMER_VOICE_NEW_TICKET_EVENT, { detail: payload }));
}

export function dispatchCustomerVoiceSettingsChanged() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CUSTOMER_VOICE_SETTINGS_CHANGED_EVENT));
}

export function parseAlertEnabled(value: string | null | undefined): boolean {
  const v = (value ?? "").trim().toLowerCase();
  if (v === "false" || v === "0" || v === "no" || v === "off") return false;
  return true;
}
